import { CampaignState } from 'core/rtbCampaign/RtbCampaign';
import {
  validateEmpty,
  validateMinimum,
  validateMinMax
} from 'utils/ValidateUtils';
import { formatPriceWithCurrency, getPriceValue } from 'helper/CurrencyHelper';
import i18n from 'i18n';
import moment from 'moment';

export interface ValidateCampaignBudgetParams {
  budget: number;
  spents: number;
  expectedSpent: number;
  campaignBudgetMinimum: number;
  currency: string;
  state?: CampaignState;
}

export function validateCampaignBudget (
  params: ValidateCampaignBudgetParams
): string | undefined {
  const {
    budget,
    spents,
    expectedSpent,
    campaignBudgetMinimum,
    currency,
    state
  } = params;

  const minBudget = Math.max(spents, expectedSpent, campaignBudgetMinimum);

  if (state === CampaignState.DEACTIVATE) {
    return validateMinimum(
      budget,
      getPriceValue(currency, spents),
      'campaign.descriptions.smallerThanBudgetMinimum',
      currency
    );
  }

  const emptyError = validateEmpty(budget);
  if (emptyError) {
    return emptyError;
  }

  let hintI18n = 'campaign.descriptions.smallerThanBudgetMinimum';
  const i18nParams: { min: string } = {
    min: formatPriceWithCurrency(currency, minBudget)
  };
  switch (minBudget) {
    case spents:
      hintI18n = 'campaign.descriptions.smallerThanSpents';
      break;
    case expectedSpent:
      hintI18n = 'campaign.descriptions.smallerThanExpectSpents';
      break;
    case campaignBudgetMinimum:
      hintI18n = 'campaign.descriptions.smallerThanCampaignBudgetMinimum';
      break;
    default:
      break;
  }

  const minValue = getPriceValue(currency, minBudget);
  if (budget < minValue) {
    return i18n.t<string>(hintI18n, { ...i18nParams });
  }
}

export interface ValidateCampaignDailyBudgetParams {
  dailyTargetBudget: number | undefined;
  budget: number;
}

export function validateCampaignDailyBudget (
  params: ValidateCampaignDailyBudgetParams
): string | undefined {
  const { dailyTargetBudget, budget } = params;
  return validateMinMax(dailyTargetBudget, Math.min(1, budget), budget);
}

export interface ValidateCampaignDateRangeParams {
  startDate: string;
  endDate: string;
  orderStartDate: string;
  orderEndDate: string;
  defaultStartDate: string;
  defaultEndDate: string;
  isNewCampaign: boolean;
}

export function validateCampaignDateRange (
  params: ValidateCampaignDateRangeParams
): string | undefined {
  const {
    startDate,
    endDate,
    orderStartDate,
    orderEndDate,
    defaultStartDate,
    defaultEndDate,
    isNewCampaign
  } = params;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays > 92) {
    return i18n.t<string>('campaign.descriptions.moreThanDays', { days: 92 });
  }

  const now = moment();
  if (moment(startDate).isBefore(orderStartDate)) {
    return i18n.t<string>('campaign.descriptions.startDayBeforeOrder');
  }
  if (moment(endDate).isAfter(moment(orderEndDate).endOf('day'))) {
    return i18n.t<string>('campaign.descriptions.endDayAfterOrder');
  }

  const shouldCheckStartDay =
    isNewCampaign || moment(defaultStartDate).isAfter(now);
  if (shouldCheckStartDay && moment(startDate).add(1, 'hours').isBefore(now)) {
    return i18n.t<string>('campaign.descriptions.createBeforeNow');
  }

  const shouldCheckEndDay = defaultEndDate !== endDate;
  if (shouldCheckEndDay && moment(endDate).isBefore(now)) {
    return i18n.t<string>('campaign.descriptions.endDayBeforeNow');
  }
}
